import { Box, Button, Spinner, Text, VStack } from "@chakra-ui/react";
import { Fragment } from "react";
import { QueryKey, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { IChatList } from "../../../types";
import { colors } from "../../../styles";
import ProfileChatListItem from "../../../components/Chat/ListItem/ProfileChatListItem";
import MyIcon from "../../../components/Icon/MyIcon";
import { ChatDeleteLoadingProvider } from "../../../components/Chat/ChatDeleteLoading";

interface ProfileChatTabPanelProps {
    queryKey: QueryKey;
    queryFn: (page: number, size: number) => Promise<IChatList>;
    enabled: boolean;
    emptyMessage: string;
}

const PAGE_SIZE = 20;

export default function ProfileChatTabPanel({
    queryKey,
    queryFn,
    enabled,
    emptyMessage,
}: ProfileChatTabPanelProps) {
    const queryClient = useQueryClient();

    const {
        data,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<IChatList>({
        queryKey,
        queryFn: ({ pageParam }) => queryFn(pageParam as number, PAGE_SIZE),
        initialPageParam: 1,
        enabled,
        getNextPageParam: (lastPage) =>
            lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined,
    });

    if (isLoading) {
        return (
            <VStack width={"100%"} justifyContent={"center"} alignItems={"center"} paddingY={10}>
                <Spinner color={colors.blue} />
            </VStack>
        );
    }

    const chats = data?.pages.flatMap((page) => page.chats) ?? [];

    if (chats.length === 0) {
        return (
            <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"} paddingY={10}>
                {emptyMessage}
            </Text>
        );
    }

    return (
        <ChatDeleteLoadingProvider
            onComplete={() => {
                queryClient.invalidateQueries({ queryKey });
                queryClient.invalidateQueries({ queryKey: ["chats"] });
                queryClient.invalidateQueries({ queryKey: ["getMyProfileChats"] });
                queryClient.invalidateQueries({ queryKey: ["getProfileChats"] });
            }}
        >
            <VStack width={"100%"} spacing={5}>
                {chats.map((chat, index) => {
                    const isFirst = index === 0;
                    const isLast = index === chats.length - 1;

                    return (
                        <ProfileChatListItem
                            queryKey={queryKey}
                            id={chat.id}
                            slug={chat.slug}
                            profileId={chat.profile.id}
                            profileImageUrl={chat.profile.avatar}
                            userType={chat.profile.user.type}
                            authorType={chat.authorType}
                            nickname={chat.profile.nickname}
                            content={chat.content}
                            imageUrls={chat.imageUrls}
                            isMyTypeOnly={chat.isMyTypeOnly}
                            likeCount={chat.likeCount}
                            commentCount={chat.commentCount}
                            saveCount={chat.saveCount}
                            isMine={chat.isMine}
                            isLiked={chat.isLiked}
                            isSaved={chat.isSaved}
                            isReported={chat.isReported}
                            isBlocking={chat.isBlocking}
                            hiddenAt={chat.hiddenAt}
                            createdAt={chat.createdAt}
                            isFirst={isFirst}
                            isLast={isLast}
                        />
                    );
                })}

                {hasNextPage && (
                    <Button
                        variant={"ghost"}
                        onClick={() => fetchNextPage()}
                        isLoading={isFetchingNextPage}
                        marginTop={5}
                    >
                        <MyIcon src={"/svg/arrow-down.svg"} alt={"더보기"} width={20} height={20} />
                    </Button>
                )}
            </VStack>
        </ChatDeleteLoadingProvider>
    );
}
